import { useState } from "react";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Navigate, Route, Routes } from "react-router-dom";
import { adminApi, getAdminSecret } from "@/lib/api";
import { AppShell } from "@/components/layout/AppShell";
import { DashboardPage } from "@/pages/DashboardPage";
import { EntityPage } from "@/pages/EntityPage";
import { ErrorsPage } from "@/pages/ErrorsPage";
import { LoginPage } from "@/pages/LoginPage";
import { SessionsPage } from "@/pages/SessionsPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { TeamPage } from "@/pages/TeamPage";

const queryClient = new QueryClient();

function AdminApp(): import("react").ReactElement {
  const [authVersion, setAuthVersion] = useState(0);
  const bootstrap = useQuery({
    queryKey: ["bootstrap", authVersion],
    queryFn: adminApi.bootstrap,
    retry: false,
    enabled: Boolean(getAdminSecret()),
  });

  if (!getAdminSecret() || bootstrap.isError) {
    return <LoginPage onReady={() => setAuthVersion((value) => value + 1)} />;
  }

  if (bootstrap.isLoading || !bootstrap.data) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Loading console...</div>;
  }

  return (
    <Routes>
      <Route element={<AppShell config={bootstrap.data} onLogout={() => setAuthVersion((value) => value + 1)} />}>
        <Route index element={<DashboardPage config={bootstrap.data} />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/crm/:entityName" element={<EntityPage config={bootstrap.data} />} />
        <Route path="/sessions" element={<SessionsPage />} />
        <Route path="/errors" element={<ErrorsPage />} />
        <Route path="/settings" element={<SettingsPage config={bootstrap.data} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App(): import("react").ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <AdminApp />
    </QueryClientProvider>
  );
}
